/**
 * Training Session Export Modal
 *
 * Modal for exporting training session data in various formats.
 * Adapted from AdvancedSimulationExportModal.js
 */

import React, { useState } from 'react';
import {
  Modal, Box, Typography, FormControlLabel, Checkbox, Button,
  Alert, CircularProgress, FormControl, InputLabel, Select,
  MenuItem, Stack, Divider, Chip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import trainingApi from '../../services/trainingApi';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 600 },
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  overflow: 'auto'
};

const TrainingSessionExportModal = ({ open, onClose, session, evaluation }) => {
  const [config, setConfig] = useState({
    format: 'pdf',
    includeEvaluation: true,
    includeFormulation: true,
    includeReflection: true,
    includeScenarioBrief: true,
    includeMetadata: true,
  });
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const handleConfigChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleExport = async () => {
    setDownloading(true);
    setError(null);
    const sessionId = session?._id;
    const sid = session?.sessionId || sessionId;

    try {
      if (config.format === 'pdf') {
        const response = await trainingApi.exportSessionPDF(sessionId, config);
        triggerDownload(new Blob([response], { type: 'application/pdf' }), `training-session-${sid}-${Date.now()}.pdf`);
      } else if (config.format === 'markdown') {
        const response = await trainingApi.exportSessionMarkdown(sessionId);
        triggerDownload(new Blob([response], { type: 'text/markdown' }), `training-session-${sid}-${Date.now()}.md`);
      } else if (config.format === 'json') {
        const response = await trainingApi.exportSessionJSON(sessionId);
        const data = response?.data || response;
        triggerDownload(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }), `training-session-${sid}-${Date.now()}.json`);
      } else if (config.format === 'csv') {
        const response = await trainingApi.exportSessionCSV(sessionId);
        triggerDownload(new Blob([response], { type: 'text/csv' }), `training-session-transcript-${sid}-${Date.now()}.csv`);
      }
      onClose();
    } catch (err) {
      console.error('Error exporting session:', err);
      setError('Failed to export session. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const turnCount = session?.transcript?.length || 0;
  const studentName = session?.studentName || session?._studentName || 'Unknown';
  const scenarioName = session?.scenarioName || session?._scenarioName || 'Unknown';
  const hasEvaluation = evaluation?.status === 'completed';
  const hasFormulation = !!session?.formulation;
  const hasReflection = !!session?.selfReflection;
  const visibilityLevel = session?.visibilityLevel || 'full_brief';
  const compositeScore = evaluation?.compositePercentage != null ? Math.round(evaluation.compositePercentage) + '%' : null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h5" component="h2" gutterBottom>
          Export Training Session
        </Typography>

        {session && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip icon={<CheckCircleIcon />} label={`${turnCount} turns`} size="small" color="primary" />
              <Chip label={`Student: ${studentName}`} size="small" variant="outlined" />
              <Chip label={scenarioName} size="small" variant="outlined" />
              <Chip
                label={visibilityLevel === 'blind' ? 'Blind' : visibilityLevel === 'referral_only' ? 'Referral Only' : 'Full Brief'}
                size="small"
                color={visibilityLevel === 'blind' ? 'error' : visibilityLevel === 'referral_only' ? 'warning' : 'success'}
                variant="outlined"
              />
              {compositeScore && (
                <Chip label={`Score: ${compositeScore}`} size="small" color="info" />
              )}
            </Stack>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2.5}>
          <FormControl fullWidth>
            <InputLabel>Export Format</InputLabel>
            <Select value={config.format} label="Export Format" onChange={handleConfigChange('format')}>
              <MenuItem value="pdf">PDF Document</MenuItem>
              <MenuItem value="markdown">Markdown</MenuItem>
              <MenuItem value="json">JSON (Full Data)</MenuItem>
              <MenuItem value="csv">CSV (Transcript Only)</MenuItem>
            </Select>
          </FormControl>

          {config.format !== 'csv' && config.format !== 'json' && (
            <>
              <Divider />
              <Typography variant="subtitle1" fontWeight="bold">Content to Include</Typography>

              <FormControlLabel
                control={<Checkbox checked disabled />}
                label="Transcript (always included)"
              />
              <FormControlLabel
                control={<Checkbox checked={config.includeEvaluation} onChange={handleConfigChange('includeEvaluation')} disabled={!hasEvaluation} />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    Evaluation Results
                    {!hasEvaluation && <Chip icon={<WarningIcon />} label="Not completed" size="small" color="warning" />}
                  </Box>
                }
              />
              <FormControlLabel
                control={<Checkbox checked={config.includeFormulation} onChange={handleConfigChange('includeFormulation')} disabled={!hasFormulation} />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    Student Formulation
                    {!hasFormulation && <Chip icon={<WarningIcon />} label="Not submitted" size="small" color="warning" />}
                  </Box>
                }
              />
              <FormControlLabel
                control={<Checkbox checked={config.includeReflection} onChange={handleConfigChange('includeReflection')} disabled={!hasReflection} />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    Self-Reflection
                    {!hasReflection && <Chip icon={<WarningIcon />} label="Not submitted" size="small" color="warning" />}
                  </Box>
                }
              />
              <FormControlLabel
                control={<Checkbox checked={config.includeScenarioBrief} onChange={handleConfigChange('includeScenarioBrief')} />}
                label="Scenario Brief"
              />
              <FormControlLabel
                control={<Checkbox checked={config.includeMetadata} onChange={handleConfigChange('includeMetadata')} />}
                label="Metadata (timestamps, configuration, rubric)"
              />
            </>
          )}

          <Divider />

          {config.format === 'json' && (
            <Alert severity="info" icon={<InfoIcon />}>
              JSON export includes all session data including internal fields and system prompts.
            </Alert>
          )}

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={onClose} disabled={downloading}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleExport}
              disabled={downloading}
              startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            >
              {downloading ? 'Exporting...' : 'Export'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

export default TrainingSessionExportModal;
